import { useMemo, useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import { Heart, Search, Star } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { messageFreelancer, toggleFavorite } from "@/lib/hireActions"
import FreelancerRow from "@/components/tuw/hire/FreelancerRow"
import SortDropdown from "@/components/tuw/hire/SortDropdown"
import FunnelDeferredDialog from "@/components/tuw/hire/FunnelDeferredDialog"

const ROLES = [
  { key: "writer", label: "Writers" },
  { key: "editor", label: "Editors" },
  { key: "designer", label: "Designers" },
  { key: "illustrator", label: "Illustrators" },
  { key: "narrator", label: "Narrators" },
  { key: "translator", label: "Translators" },
]

const SORTS = [
  { key: "recent", label: "Recently saved" },
  { key: "rating", label: "Highest rated" },
  { key: "availability", label: "Soonest available" },
  { key: "name", label: "Name A–Z" },
]

function sortItems(list, sort) {
  const copy = [...list]
  switch (sort) {
    case "rating":
      return copy.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1))
    case "availability":
      return copy.sort((a, b) => (a.availability?.date || "9999").localeCompare(b.availability?.date || "9999"))
    case "name":
      return copy.sort((a, b) => a.penName.localeCompare(b.penName))
    default:
      return copy // already saved-recent-first
  }
}

export default function Favorites() {
  const { favorites, auth } = usePage().props
  const canMessage = auth?.canMessage ?? true
  const [removed, setRemoved] = useState(() => new Set())
  const [roleFilter, setRoleFilter] = useState("all")
  const [sort, setSort] = useState("recent")
  const [funnel, setFunnel] = useState({ open: false, freelancer: null })

  const live = useMemo(() => favorites.items.filter((f) => !removed.has(f.id)), [favorites.items, removed])

  const counts = useMemo(() => {
    const c = { all: live.length }
    ROLES.forEach((r) => (c[r.key] = live.filter((f) => f.type === r.key).length))
    return c
  }, [live])

  const filtered = roleFilter === "all" ? live : live.filter((f) => f.type === roleFilter)
  const rows = sortItems(filtered, sort)

  const onRemove = (f) => {
    setRemoved((cur) => new Set(cur).add(f.id))
    toggleFavorite(f.id, { only: ["navCounts"] })
  }

  const onMessage = (f) => f && messageFreelancer(f.id)
  const chips = [{ key: "all", label: "All" }, ...ROLES.filter((r) => counts[r.key] > 0)]

  if (live.length === 0) {
    return (
      <>
        <div className="flex max-w-[1080px] flex-col gap-[16px] px-[30px] pt-[22px] pb-[60px] max-[1025px]:px-[16px]">
          <div>
            <h1 className="font-display text-[25px] font-bold tracking-[-0.5px]">Favorites</h1>
            <p className="mt-1 max-w-[560px] text-[13px] leading-[1.5] text-ink-3">
              Freelancers you've saved — your shortlist, ready to invite to your next project.
            </p>
          </div>
          <div className="rounded-[15px] border border-border bg-card px-[24px] py-[64px] text-center">
            <div className="mx-auto mb-4 flex size-[58px] items-center justify-center rounded-[16px] border border-border bg-secondary">
              <Heart className="size-[27px] text-ink-3" strokeWidth={1.7} />
            </div>
            <h3 className="font-display text-[18px] font-bold">No saved freelancers yet</h3>
            <p className="mx-auto mt-[9px] max-w-[380px] text-[13.5px] leading-[1.55] text-ink-3">
              Tap the heart on any freelancer in Find Freelancers to save them here — your shortlist for inviting to projects.
            </p>
            <div className="mt-[20px] flex justify-center">
              <Button asChild>
                <Link href="/find-freelancers">
                  <Search className="size-4" strokeWidth={2} />
                  Browse freelancers
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* P8-H7 "Muster": the saved list becomes cards at ≤1024 — its own
          breakpoint, not Find Freelancers' 1200, because this column is capped
          at 1080 and carries no filter rail. `max-[1025px]` is the exclusive
          edge for "at or below 1024" (see FreelancerRow). */}
      <div className="flex max-w-[1080px] flex-col gap-[16px] px-[30px] pt-[22px] pb-[60px] max-[1025px]:px-[16px]">
        <div className="flex items-end justify-between gap-[16px] max-[1025px]:flex-wrap">
          <div>
            <h1 className="font-display text-[25px] font-bold tracking-[-0.5px]">Favorites</h1>
            <p className="mt-1 max-w-[560px] text-[13px] leading-[1.5] text-ink-3">
              Freelancers you've saved — your shortlist, ready to invite to your next project.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link href="/find-freelancers">
              <Search className="size-4" strokeWidth={2} />
              Find more freelancers
            </Link>
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-[10px]">
          <div className="flex flex-1 flex-wrap items-center gap-[7px]">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setRoleFilter(c.key)}
                className={cn(
                  "inline-flex items-center gap-[7px] rounded-full border px-[13px] py-[6px] text-[12.5px] font-semibold transition-colors",
                  roleFilter === c.key ? "border-transparent bg-foreground text-background" : "border-transparent bg-secondary text-muted-foreground hover:border-line-2"
                )}
              >
                {c.label}
                <span className="text-[11px] font-bold opacity-60">{counts[c.key]}</span>
              </button>
            ))}
          </div>
          <SortDropdown value={sort} options={SORTS} onPick={setSort} />
        </div>

        <div className="text-[13px] font-semibold text-muted-foreground">
          <b className="font-display text-foreground">{live.length}</b> saved freelancer{live.length !== 1 ? "s" : ""}
        </div>

        {rows.length === 0 ? (
          <div className="rounded-[15px] border border-border bg-card px-[20px] py-[48px] text-center">
            <div className="mx-auto mb-3 flex size-[48px] items-center justify-center rounded-[14px] bg-secondary">
              <Star className="size-[22px] text-ink-3" strokeWidth={1.7} />
            </div>
            <h3 className="font-display text-[15px] font-bold">
              No {ROLES.find((r) => r.key === roleFilter)?.label.toLowerCase()} saved
            </h3>
            <p className="mt-[6px] text-[13px] text-ink-3">You haven't favorited anyone in this role yet.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-[15px] border border-border bg-card max-[1025px]:flex max-[1025px]:flex-col max-[1025px]:gap-[12px] max-[1025px]:overflow-visible max-[1025px]:rounded-none max-[1025px]:border-0 max-[1025px]:bg-transparent">
            {rows.map((f) => (
              <FreelancerRow
                key={f.id}
                f={f}
                cardBelow={1024}
                isFavorite
                savedLabel={f.savedLabel}
                onToggleFavorite={onRemove}
                onInvite={(fl) => setFunnel({ open: true, freelancer: fl })}
              />
            ))}
            <div className="flex items-center justify-between border-t border-border px-[22px] py-[15px] text-[12.5px] font-semibold text-ink-3 max-[1025px]:flex-wrap max-[1025px]:gap-[10px] max-[1025px]:border-t-0 max-[1025px]:px-[2px] max-[1025px]:py-0">
              <span>{rows.length} saved freelancer{rows.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
        )}
      </div>

      <FunnelDeferredDialog
        open={funnel.open}
        onOpenChange={(open) => setFunnel((s) => ({ ...s, open }))}
        freelancer={funnel.freelancer}
        kind="invite"
        onMessage={onMessage}
        canMessage={canMessage}
        onSave={() => {}}
        isFavorite
      />
    </>
  )
}

Favorites.layout = (page) => (
  <AppShell active="Favorites" crumb={["Hire", "Favorites"]}>
    {page}
  </AppShell>
)
